import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import {
  ArrowLeft,
  Compass,
  Target,
  Telescope,
  CalendarClock,
  Sparkles,
  Flag,
} from "lucide-react";
import { EditableVisionBlock } from "@/components/vision-compass";
import {
  MilestoneList,
  useMilestones,
  milestoneProgress,
  openAddMilestone,
  type Horizon,
} from "@/components/vision-milestones";
import { formatDate } from "@/lib/format";

export const Route = createFileRoute("/_authenticated/vision")({
  head: () => ({
    meta: [
      { title: "Vision — The Wealthy Creatives Boardroom" },
      {
        name: "description",
        content:
          "Your full Vision — the 10-year North Star and 1-year Horizon that guide every decision, with milestones to track the path.",
      },
      { property: "og:title", content: "Vision — Wealthy Creatives Boardroom" },
      {
        property: "og:description",
        content: "Expand your compass. See the full picture of where you're going.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: VisionPage,
});

const FALLBACK_ONE = "Building a legacy of creative autonomy and world-class craft.";
const FALLBACK_TEN = "A life of creative freedom, impact, and generational wealth.";

function VisionPage() {
  const { user } = useAuth();
  if (!user) return null;

  const { data: profile } = useQuery({
    queryKey: ["profile-vision-full", user.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("one_year_vision, ten_year_vision, full_name")
        .eq("id", user.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const { data: milestones = [] } = useMilestones(user.id);
  const oneProg = milestoneProgress(milestones, "one_year");
  const tenProg = milestoneProgress(milestones, "ten_year");
  const totalMilestones = milestones.length;
  const totalDone = milestones.filter((m) => !!m.completed_at).length;

  const upcoming = [...milestones]
    .filter((m) => !m.completed_at && m.target_date)
    .sort((a, b) => (a.target_date! < b.target_date! ? -1 : 1))
    .slice(0, 3);

  const today = new Date();

  return (
    <div className="min-h-[100dvh] bg-[color:var(--background)]">
      {/* Top bar */}
      <div className="sticky top-0 z-20 border-b border-[color:var(--hairline)] bg-[color:var(--background)]/85 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-[color:var(--surface-2)] hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </Link>
          <div className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-[color:var(--gold)]">
            <Compass className="h-3.5 w-3.5" /> Vision
          </div>
          <div className="w-[92px] text-right text-[10px] uppercase tracking-wider text-muted-foreground">
            {today.toLocaleDateString("en-US", { weekday: "short" })} · {formatDate(today)}
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[color:var(--hairline)]">
        <div className="pointer-events-none absolute inset-0 opacity-[0.35]">
          <div className="absolute top-1/2 left-0 h-px w-full bg-gradient-to-r from-transparent via-[color:var(--gold)] to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(201,168,76,0.15),transparent_60%)]" />
        </div>
        <div className="relative mx-auto max-w-5xl px-4 py-10 sm:py-14">
          <div className="flex flex-col items-center text-center">
            <div className="relative mb-4">
              <div className="absolute inset-0 rounded-full bg-[color:var(--gold)] opacity-20 blur-2xl" />
              <div className="relative grid h-16 w-16 place-items-center rounded-full border border-[color:var(--gold)]/40 bg-[color:var(--background)] shadow-[0_0_30px_rgba(201,168,76,0.25)]">
                <Compass className="h-8 w-8 text-[color:var(--gold)]" />
              </div>
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Your Vision
            </h1>
            <p className="mt-2 max-w-xl text-sm text-muted-foreground">
              The full compass. Refine what you're building, break it into milestones, and
              keep every decision aligned with your North Star.
            </p>
            <p className="mt-3 max-w-lg text-[11px] text-muted-foreground/80">
              Milestones are how you check progress toward your Vision. Add 2–5 for each
              horizon, then tap the circle beside one to mark it done.
            </p>
          </div>

          {/* Summary strip */}
          {totalMilestones === 0 ? (
            <div className="mt-8 flex flex-col items-center gap-3 rounded-2xl border border-dashed border-[color:var(--gold)]/40 bg-[color:var(--gold)]/5 p-6 text-center">
              <p className="text-sm font-medium text-foreground/90">
                No milestones yet — turn your Vision into checkable steps.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                <button
                  type="button"
                  onClick={() => openAddMilestone("one_year")}
                  className="inline-flex items-center gap-1.5 rounded-md bg-[color:var(--gold)] px-3 py-2 text-xs font-semibold text-black hover:bg-[color:var(--gold)]/90"
                >
                  <Flag className="h-3.5 w-3.5" /> Add 1-Year milestone
                </button>
                <button
                  type="button"
                  onClick={() => openAddMilestone("ten_year")}
                  className="inline-flex items-center gap-1.5 rounded-md border border-[color:var(--gold)]/50 bg-transparent px-3 py-2 text-xs font-semibold text-[color:var(--gold)] hover:bg-[color:var(--gold)]/10"
                >
                  <Telescope className="h-3.5 w-3.5" /> Add 10-Year milestone
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-8 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
              <StatCard
                icon={<Flag className="h-3.5 w-3.5" />}
                label="Milestones"
                value={`${totalDone}/${totalMilestones}`}
                hint="Completed"
              />
              <StatCard
                icon={<Target className="h-3.5 w-3.5" />}
                label="1-Year"
                value={`${oneProg.pct}%`}
                hint={`${oneProg.done}/${oneProg.total} done`}
              />
              <StatCard
                icon={<Telescope className="h-3.5 w-3.5" />}
                label="10-Year"
                value={`${tenProg.pct}%`}
                hint={`${tenProg.done}/${tenProg.total} done`}
              />
              <StatCard
                icon={<CalendarClock className="h-3.5 w-3.5" />}
                label="Next up"
                value={upcoming[0] ? formatShortDate(upcoming[0].target_date!) : "—"}
                hint={upcoming[0]?.title ?? "No dated milestones"}
                truncateHint
              />
            </div>
          )}
        </div>
      </section>

      {/* Body */}
      <section className="mx-auto max-w-5xl px-4 py-8">
        <div className="grid gap-6 md:grid-cols-2">
          <HorizonPanel
            variant="ten"
            title="10-Year North Star"
            subtitle="Where every year adds up to."
            icon={<Telescope className="h-4 w-4" />}
            value={profile?.ten_year_vision ?? ""}
            fallback={FALLBACK_TEN}
            field="ten_year_vision"
            horizon="ten_year"
            label="10-Year"
            progressPct={tenProg.pct}
            userId={user.id}
          />
          <HorizonPanel
            variant="one"
            title="1-Year Horizon"
            subtitle="What this year makes real."
            icon={<Target className="h-4 w-4" />}
            value={profile?.one_year_vision ?? ""}
            fallback={FALLBACK_ONE}
            field="one_year_vision"
            horizon="one_year"
            label="1-Year"
            progressPct={oneProg.pct}
            userId={user.id}
          />
        </div>

        {/* Upcoming */}
        {upcoming.length > 0 && (
          <div className="mt-8 rounded-2xl border border-[color:var(--hairline)] bg-[color:var(--surface-1)]/60 p-5">
            <div className="mb-3 flex items-center gap-2 text-[color:var(--gold)]">
              <Sparkles className="h-4 w-4" />
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em]">
                Coming up
              </p>
            </div>
            <ul className="divide-y divide-[color:var(--hairline)]">
              {upcoming.map((m) => (
                <li key={m.id} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground/90">
                      {m.title}
                    </p>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {m.horizon === "ten_year" ? "10-Year" : "1-Year"} milestone
                    </p>
                  </div>
                  <div className="shrink-0 rounded-md border border-[color:var(--gold)]/25 bg-[color:var(--gold)]/10 px-2 py-1 text-[11px] font-semibold text-[color:var(--gold)]">
                    {formatShortDate(m.target_date!)}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  );
}

function HorizonPanel({
  variant,
  title,
  subtitle,
  icon,
  value,
  fallback,
  field,
  horizon,
  label,
  progressPct,
  userId,
}: {
  variant: "one" | "ten";
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  value: string;
  fallback: string;
  field: "one_year_vision" | "ten_year_vision";
  horizon: Horizon;
  label: string;
  progressPct: number;
  userId: string;
}) {
  const accent = variant === "ten" ? "var(--gold)" : "var(--teal)";
  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-[color:var(--hairline)] bg-[color:var(--surface-1)]/70 p-5"
      style={{ boxShadow: `inset 0 1px 0 color-mix(in oklab, ${accent} 15%, transparent)` }}
    >
      <div
        className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full blur-3xl"
        style={{ background: `color-mix(in oklab, ${accent} 25%, transparent)` }}
      />
      <div className="relative">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div
              className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em]"
              style={{ color: `color-mix(in oklab, ${accent} 85%, white)` }}
            >
              {icon}
              {title}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
          </div>
          <ProgressRing pct={progressPct} accent={accent} />
        </div>

        <EditableVisionBlock
          icon={icon}
          label={label}
          value={value}
          placeholder={fallback}
          field={field}
          userId={userId}
        />

        <div className="mt-5 border-t border-[color:var(--hairline)] pt-4">
          <MilestoneList userId={userId} horizon={horizon} label={label} />
        </div>
      </div>
    </div>
  );
}

function ProgressRing({ pct, accent }: { pct: number; accent: string }) {
  const r = 20;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.min(100, Math.max(0, pct)) / 100) * c;
  return (
    <div className="relative shrink-0">
      <svg width="52" height="52" viewBox="0 0 52 52" className="-rotate-90">
        <circle
          cx="26"
          cy="26"
          r={r}
          stroke="var(--hairline)"
          strokeWidth="4"
          fill="none"
        />
        <circle
          cx="26"
          cy="26"
          r={r}
          stroke={accent}
          strokeWidth="4"
          strokeLinecap="round"
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 500ms ease" }}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center text-[10px] font-bold text-foreground">
        {pct}%
      </div>
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  hint,
  truncateHint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  truncateHint?: boolean;
}) {
  return (
    <div className="rounded-xl border border-[color:var(--hairline)] bg-[color:var(--surface-1)]/60 p-3">
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        <span className="text-[color:var(--gold)]">{icon}</span>
        {label}
      </div>
      <div className="mt-1 font-display text-lg font-bold text-foreground">{value}</div>
      {hint && (
        <div
          className={`text-[10px] text-muted-foreground ${
            truncateHint ? "truncate" : ""
          }`}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

function formatShortDate(d: string) {
  const dt = new Date(d + "T00:00:00");
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
